import { ShieldCheck } from "lucide-react";
import { articles } from "../content";
import { ArticleCard } from "../components/ArticleCard";
import { ButtonLink } from "../components/ButtonLink";
import { Callout } from "../components/Callout";
import { SectionHeading } from "../components/SectionHeading";
import { consultationSteps, Steps } from "../components/Steps";
import { TestPanel } from "../components/TestPanel";

const symptomLinks = [
  { label: "The water turned green", detail: "Light, nutrients, filtration", href: "/why-is-my-fish-tank-green/" },
  { label: "A fish stopped eating", detail: "Stress, water quality, illness", href: "/why-is-my-fish-not-eating/" },
  { label: "Cloudy eyes", detail: "Water quality, infection, injury", href: "/why-are-my-fishs-eyes-cloudy/" },
  { label: "The cycle will not settle", detail: "Ammonia, nitrite, nitrate", href: "/what-is-the-nitrogen-cycle/" },
  { label: "Something is leaking", detail: "Plumbing, seams, pumps", href: "/stop-aquarium-leaks-fast/" },
] as const;

const tickerItems = [
  "Green water",
  "Cloudy eyes",
  "Fish not eating",
  "Leaks",
  "Stalled cycle",
  "Algae",
  "Filtration",
  "Water changes",
  "Turtles",
  "Paludariums",
  "Aquascaping",
] as const;

const credentials = [
  ["25+", "years servicing aquariums"],
  ["7", "days a week, 8am to 9pm PT"],
  ["15", "focused minutes per call"],
  ["1", "clear next step to take home"],
] as const;

export function HomePage() {
  const [lead, ...rest] = articles;

  return (
    <>
      <section className="hero">
        <div className="hero__grid wrap">
          <div className="hero__copy">
            <p className="index-label hero__label">
              <span className="index-label__number">Field manual</span>
              <span>Southern California, since 1998</span>
            </p>
            <h1 className="display hero__title">
              Ten forum answers. <em>One that fits your tank.</em>
            </h1>
            <p className="hero__lede">
              Show an experienced aquarium professional what you are seeing, share your water readings, and get practical next steps in a focused 15-minute video call.
            </p>
            <div className="hero__actions">
              <ButtonLink href="/consultation/">Request a consultation</ButtonLink>
              <ButtonLink href="#symptoms" variant="secondary">Start with the symptom</ButtonLink>
            </div>
            <p className="hero__note"><ShieldCheck aria-hidden="true" size={18} /> Advice first. No equipment upsell.</p>
          </div>
          <div className="hero__visual">
            <figure className="hero__figure">
              <img
                src="/media/optimized/david-clode-IKFVzqVNGK0-unsplash.webp"
                alt="A porcupine puffer fish looking straight at the camera in a dark aquarium"
                width={1200}
                height={800}
                fetchPriority="high"
              />
              <figcaption>The look every keeper knows.</figcaption>
            </figure>
            <TestPanel />
          </div>
        </div>
      </section>

      <div className="ticker" aria-hidden="true">
        <div className="ticker__track">
          {[0, 1].map((copy) => (
            <ul className="ticker__list" key={copy}>
              {tickerItems.map((item) => <li key={item}>{item}</li>)}
            </ul>
          ))}
        </div>
      </div>

      <section className="symptoms wrap" id="symptoms" aria-labelledby="symptom-heading">
        <SectionHeading index="01" label="Start with the symptom" title="What are you seeing?" id="symptom-heading">
          Start with a practical guide. If your tank does not fit the typical pattern, send the details and we will help you narrow it down.
        </SectionHeading>
        <ol className="symptom-index">
          {symptomLinks.map(({ label, detail, href }, index) => (
            <li key={href}>
              <a className="symptom-index__row" href={href}>
                <span className="symptom-index__number">{String(index + 1).padStart(2, "0")}</span>
                <span className="symptom-index__label">{label}</span>
                <span className="symptom-index__detail">{detail}</span>
                <span className="symptom-index__cue" aria-hidden="true">Read the guide</span>
              </a>
            </li>
          ))}
        </ol>
      </section>

      <section className="how-it-works" aria-labelledby="how-heading">
        <div className="wrap">
          <SectionHeading
            index="02"
            label="How a consult works"
            title="Fifteen minutes, one camera, one plan."
            id="how-heading"
            aside={<ButtonLink href="/how-it-works/" variant="text">Full details</ButtonLink>}
          />
          <Steps steps={consultationSteps} />
        </div>
      </section>

      <section className="featured-guides wrap" aria-labelledby="guide-heading">
        <SectionHeading
          index="03"
          label="From the field guide"
          title="Learn the system, not just the quick fix."
          id="guide-heading"
          aside={<ButtonLink href="/articles/" variant="text">Browse all guides</ButtonLink>}
        />
        <div className="featured-guides__grid">
          <ArticleCard article={lead} featured />
          <div className="featured-guides__list">
            {rest.slice(0, 4).map((article) => <ArticleCard article={article} key={article.id} />)}
          </div>
        </div>
      </section>

      <section className="expert wrap" aria-labelledby="expert-heading">
        <div className="expert__media">
          <img src="/media/optimized/clients-howie.webp" alt="Aquarium expert Brian Hoover with client Howie Mandel" loading="lazy" width={600} height={620} />
          <span className="expert__tag">Brian Hoover · Aquatic Contractors</span>
        </div>
        <div className="expert__copy">
          <SectionHeading index="04" label="Who you will talk to" title="Experience that is useful in the next fifteen minutes." id="expert-heading" />
          <p>Brian Hoover of Aquatic Contractors has spent more than 25 years building and servicing complex aquariums across Southern California. Aquarium Helpline turns that field experience into direct, focused troubleshooting you can use at home.</p>
          <blockquote className="expert__quote">
            “Most calls can be diagnosed over video once we can see the tank and the water readings.”
          </blockquote>
          <dl className="credentials">
            {credentials.map(([figure, caption]) => (
              <div className="credentials__item" key={caption}>
                <dt className="display">{figure}</dt>
                <dd>{caption}</dd>
              </div>
            ))}
          </dl>
          <ButtonLink href="/about/" variant="text">Why we built Aquarium Helpline</ButtonLink>
        </div>
      </section>

      <div className="wrap callout-wrap"><Callout /></div>
    </>
  );
}
